import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Text, Card, Image, Button, Loading, Spacer, Grid, Row, Modal, Input, Dropdown } from "@nextui-org/react";
import { Upload, RefreshCw, Copy, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import WorkflowTimeline, { WorkshopTransition } from '../../components/WorkflowTimeline/WorkflowTimeline';
import WorkshopParticipantsList from '../../components/WorkshopParticipantsList/WorkshopParticipantsList';
import { getWorkshopById } from "../../config/workshopsApi";
import { syncWorkshop, generateAnonymousParticipants } from "../../config/workshopApi";
import { WorkshopUser } from '../../services/workshopService';
import { Workshop } from '../../types/types';

const WorkshopDetails = () => {
  const { workshopId } = useParams<{ workshopId: string }>();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const [participants, setParticipants] = useState<WorkshopUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'workflow' | 'users'>('workflow');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // State for Generate Anonymous Participants Dialog
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [participantCount, setParticipantCount] = useState<number | string>('');
  const [participantRoles, setParticipantRoles] = useState('');
  const [clusteringMethod, setClusteringMethod] = useState(new Set(["Default"]));
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateParticipants = async () => {
    if (!workshopId) return;

    const count = parseInt(participantCount as string, 10);
    if (isNaN(count) || count <= 0) {
      toast.error('Please enter a valid number of participants.');
      return;
    }

    if (!participantRoles.trim()) {
      toast.error('Please enter at least one role.');
      return;
    }

    setIsGenerating(true);
    try {
      const selectedMethod = Array.from(clusteringMethod)[0] as string;
      await generateAnonymousParticipants(
        workshopId,
        count,
        participantRoles,
        selectedMethod
      );
      toast.success('Anonymous participants generated successfully.');
      setIsGenerateDialogOpen(false);
      // Optionally, refetch data here
    } catch (error) {
      console.error('Error generating participants:', error);
      toast.error('Failed to generate participants.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleWorkshopSync = async () => {
    if (!workshopId || !workshop || !workshop.key) return;
    setIsSyncing(true);
    try {
      const res = await syncWorkshop(workshop.key);
      console.log("handleWorkshopSync", res)
      if (res === "1")
        toast.success('Workshop synced successfully');
      else
        toast.error('Workshop sync error');
    } catch (error) {
      console.error('Error syncing workshop :', error);
      toast.error('Failed to sync workshop');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      // Placeholder for API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Workshop duplicated successfully');
    } catch (error) {
      console.error('Error duplicating workshop:', error);
      toast.error('Failed to duplicate workshop');
    } finally {
      setIsDuplicating(false);
    }
  };

  useEffect(() => {
    const fetchWorkshopAndUsers = async (_workshopId: string) => {
      setIsLoading(true);

      try {
        const rawData = await getWorkshopById(_workshopId);
        const data = rawData?.result || rawData;

        if (data) {
          setWorkshop(data);
          const participantsData = data.participants || {};
          setParticipants(
            Array.isArray(participantsData) ? participantsData : Object.values(participantsData)
          );
        }
      } catch (error) {
        console.error("Error fetching workshops data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (workshopId) {
      fetchWorkshopAndUsers(workshopId);
    }
  }, [workshopId]);

  if (isLoading) {
    return (
      <Container display="flex" justify="center" alignItems="center" css={{ height: "50vh" }}>
        <Loading type="points" />
      </Container>
    );
  }

  if (!workshop) {
    return (
      <Container>
        <Spacer y={2} />
        <Text h3>Workshop not found</Text>
        <Button onClick={() => navigate('/workshops')}>Back to List</Button>
      </Container>
    );
  }

  return (
    <Container md>
      <Spacer y={1} />
      <Row justify="space-between" align="center">
        <Button auto flat onClick={() => navigate('/workshops')}>
          ← Back to Workshops
        </Button>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button auto color="secondary" onClick={handleWorkshopSync} disabled={isSyncing}>
            <RefreshCw size={20} className={isSyncing ? 'animate-spin' : ''} />
            <Spacer x={0.5} />
            {isSyncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button auto color="secondary" onClick={() => setIsGenerateDialogOpen(true)}>
            <Users size={20} />
            <Spacer x={0.5} />
            Generate Anonymous Participant List
          </Button>
          <Button auto color="warning" onClick={handleDuplicate} disabled={isDuplicating}>
            <Copy size={20} className={isDuplicating ? 'animate-pulse' : ''} />
            <Spacer x={0.5} />
            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
          </Button>
          <Button auto color="primary" onClick={() => navigate('import')}>
            <Upload size={20} />
            <Spacer x={0.5} />
            Import Users
          </Button>
        </div>
      </Row>
      <Spacer y={1} />

      {/* Generate Anonymous Participants Dialog */}
      <Modal
        closeButton
        aria-labelledby="generate-modal-title"
        open={isGenerateDialogOpen}
        onClose={() => setIsGenerateDialogOpen(false)}
      >
        <Modal.Header>
          <Text id="generate-modal-title" size={18} b>
            Generate Anonymous Participants
          </Text>
        </Modal.Header>
        <Modal.Body>
          <Input
            clearable
            bordered
            fullWidth
            type="number"
            color="primary"
            size="lg"
            placeholder="Number of participants"
            label="Participants Count"
            value={participantCount as string}
            onChange={(e) => setParticipantCount(e.target.value)}
          />
          <Input
            clearable
            bordered
            fullWidth
            color="primary"
            size="lg"
            placeholder="e.g. Developer, Designer, Manager"
            label="Roles (comma-separated)"
            value={participantRoles}
            onChange={(e) => setParticipantRoles(e.target.value)}
          />
          <Text size={14} css={{ marginTop: '$4', marginBottom: '$2' }}>
            Clustering Method
          </Text>
          <Dropdown>
            <Dropdown.Button flat color="primary" css={{ tt: "capitalize" }}>
              {Array.from(clusteringMethod)[0]}
            </Dropdown.Button>
            <Dropdown.Menu
              aria-label="Clustering Method"
              color="primary"
              disallowEmptySelection
              selectionMode="single"
              selectedKeys={clusteringMethod}
              onSelectionChange={(keys) => setClusteringMethod(keys as Set<string>)}
            >
              <Dropdown.Item key="Default">Default</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Modal.Body>
        <Modal.Footer>
          <Button auto flat color="error" onClick={() => setIsGenerateDialogOpen(false)}>
            Cancel
          </Button>
          <Button auto onClick={handleGenerateParticipants} disabled={isGenerating}>
            {isGenerating ? <Loading type="spinner" color="currentColor" size="sm" /> : "Generate"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Card>
        <Card.Body>
          <Grid.Container gap={2}>
            <Grid xs={12} md={4}>
              <Image
                src={workshop.eventCoverUrl || "https://via.placeholder.com/400x300?text=Workshop"}
                objectFit="cover"
                width="100%"
                height={200}
                alt={workshop.title}
              />
            </Grid>
            <Grid xs={12} md={8} direction="column">
              <Text h2>{workshop.title}</Text>
              <Spacer y={0.5} />
              <Text b>Status: </Text>
              <Text span css={{ color: workshop.status === 'published' ? '$success' : '$warning' }}>
                {workshop.status.toUpperCase()}
              </Text>
              <Spacer y={0.5} />
              <Text b>Date: </Text>
              <Text span>{new Date(workshop.date).toLocaleString()}</Text>
              <Spacer y={1} />
              <Text>{workshop.description}</Text>
            </Grid>
          </Grid.Container>
        </Card.Body>
      </Card>

      <Spacer y={2} />

      <Row>
        <Button.Group>
          <Button
            flat={activeTab !== 'workflow'}
            onClick={() => setActiveTab('workflow')}
          >
            Workflow
          </Button>
          <Button
            flat={activeTab !== 'users'}
            onClick={() => setActiveTab('users')}
          >
            Users
          </Button>
        </Button.Group>
      </Row>

      <Spacer y={1} />

      {activeTab === 'workflow' && (
        <Card>
          <Card.Body>
            <WorkflowTimeline
              steps={WORKFLOW_STEPS}
              transitions={MOCK_TRANSITIONS.filter(t => t.id_workshop === workshopId) || []}
            />
          </Card.Body>
        </Card>
      )}

      {activeTab === 'users' && (
        <Card>
          <Card.Body>
            <WorkshopParticipantsList
              workshopId={workshopId || ''}
              users={participants}
            />
          </Card.Body>
        </Card>
      )}

    </Container>
  );
};

const WORKFLOW_STEPS = ['Draft', 'Review', 'Published', 'Live', 'Completed'];


const MOCK_TRANSITIONS: WorkshopTransition[] = [
  { id_workshop: "1", source_state: "Draft", destination_state: "Draft", timestamp: new Date(Date.now() - 100000000).toISOString(), id_user: "u1" },
  { id_workshop: "1", source_state: "Draft", destination_state: "Review", timestamp: new Date(Date.now() - 50000000).toISOString(), id_user: "u1" },
  { id_workshop: "1", source_state: "Review", destination_state: "Published", timestamp: new Date(Date.now() - 10000000).toISOString(), id_user: "u2" },
  { id_workshop: "2", source_state: "Draft", destination_state: "Draft", timestamp: new Date(Date.now() - 100000000).toISOString(), id_user: "u1" },
];

export default WorkshopDetails;
